import React, { useState, useEffect, FunctionComponent, useRef, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { parse } from 'query-string';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import {
  fetchReferenceSurveyRoutine,
  startSurveyRoutine,
  confirmReferenceRoutine,
  checkSurveyKeyRoutine,
  setRefContactInfoRoutine,
  checkSurveyCodeRoutine
} from '../../routines';
import Intro from '../Intro';
import { IntroPages, Pages } from './utils';
import { IBindingCallback1, IBindingAction } from '../../../../common/models/callback';
import QuestionsContainer from '../QuestionsContainer';
import { IAppState } from '../../../../common/models/store/IAppState';
import { IReferenceSurvey } from '../../../../common/models/reference/IReferenceSurvey';
import ThankPage from '../ThankPage';
import ConfirmRelationshipForm from '../ConfirmRelationshipForm';
import LoaderWrapper from '../../../../components/LoaderWrapper';
import Prospect from '../Prospect';
import ConfirmContactInfoForm from '../../components/ConfirmContactInfoForm';
import { usePrevious } from '../../../../common/helpers/customHooks';
import { defaultCurrentPageIndex, fromQuestionsToPages, ISurveyUIPage } from '../QuestionsContainer/utils';
import { IQuestionForReference } from '../../../../common/models/question/IQuestionForReference';
import { ProspectPages } from '../Prospect/utils';
import { IContactMainInfo } from '../../../../common/models/contact/IContactMainInfo';
import ProgressBar from '../../../CandidateForm/components/ProgressBar';
import IconButton from '../../../../components/IconButton';
import Icon from '../../../../components/Icon';
import { setWorkspaceColorsRoutine } from '../../../../routines/workspace';
import { getBackgroundURL } from '../../../../common/helpers/backgroundHelper';
import { WorkspaceSettings } from '../../../../common/models/workspace/WorkspaceSettings';
import styles from './styles.module.scss';
import CheckCode from '../../../../components/CheckCode';

interface ISurveyProps extends RouteComponentProps {
  reference: IReferenceSurvey;
  surveyStartDate?: Date;
  surveyCompleteDate?: Date;
  isPreview?: boolean;
  error?: string | null;
  isConfirming: boolean;
  questions: IQuestionForReference[];
  candidateFirstName: string;
  settings: WorkspaceSettings;
  logo: string;
  isChecking: boolean;
  errorCode: undefined | string;
  isManualInput: boolean;
  checkCode: IBindingCallback1<string>;
  fetchReferenceSurvey: IBindingAction;
  startSurvey: IBindingCallback1<{ id: string; isManualInput: boolean; }>;
  confirmReference: IBindingCallback1<{ data: IReferenceSurvey, isManualInput: boolean }>;
  checkKey: IBindingAction;
  setContactInfo: IBindingCallback1<Omit<IContactMainInfo, 'id'>>;
  setWorkspaceColors: IBindingCallback1<boolean>;
}

const Survey: FunctionComponent<ISurveyProps> = ({
  isChecking,
  errorCode,
  reference,
  isPreview = false,
  surveyStartDate,
  surveyCompleteDate,
  error,
  isConfirming,
  questions,
  candidateFirstName,
  settings,
  logo,
  location,
  history,
  isManualInput,
  fetchReferenceSurvey,
  startSurvey,
  confirmReference,
  checkCode,
  setContactInfo,
  checkKey,
  setWorkspaceColors
}) => {
  const [currentPage, setCurrentPage] = useState<Pages | null>(null);
  const [currentIntroPage, setCurrentIntroPage] = useState(IntroPages.WelcomeScreen);
  const [currentQuestionPageIndex, setCurrentQuestionPageIndex] = useState(0);
  const [questionPages, setQuestionPages] = useState<ISurveyUIPage[]>([]);
  const [currentProspectPage, setCurrentProspectPage] = useState<ProspectPages>(ProspectPages.Intro);
  const [animationClass, setAnimationClass] = useState<'left' | 'right'>('left');
  const ref = useRef<HTMLDivElement>(null);
  const queryParams = parse(location.search);

  if (!queryParams.key && !isPreview && !isManualInput) {
    history.push('/');
    return null;
  }

  const onChangePage = (page: Pages, goBack?: boolean) => {
    setAnimationClass(goBack ? 'right' : 'left');
    setCurrentPage(page);
  };

  const onChangeIntroPage = (page: IntroPages, goBack?: boolean) => {
    setAnimationClass(goBack ? 'right' : 'left');
    setCurrentIntroPage(page);
  };

  const onChangeQuestionPageIndex = (index: number, goBack?: boolean) => {
    setAnimationClass(goBack ? 'right' : 'left');
    setCurrentQuestionPageIndex(index);
  };

  useEffect(() => {
    if (!isPreview && !isManualInput) {
      if (!queryParams.code && queryParams.key) {
        checkKey();
      }
      if (queryParams.code && !reference?.id) {
        fetchReferenceSurvey();
      }
    }

    setWorkspaceColors(true);
    return () => {
      setWorkspaceColors(false);
    };
  }, []);

  useEffect(() => {
    if (error) {
      history.push('/');
    }
  }, [error]);

  const changePageToDefault = () => {
    if (surveyCompleteDate) onChangePage(Pages.ThankPage);
    else if (!reference.title && surveyStartDate) onChangePage(Pages.ConfirmContactInfo);
    else if (surveyStartDate) onChangePage(Pages.Questions);
    else onChangePage(Pages.Intro);
  };

  const changeManualInputPageToDefault = () => {
    if (surveyCompleteDate) history.goBack();
    else if (surveyStartDate && reference.title) onChangePage(Pages.Questions);
    else onChangePage(Pages.ConfirmRelationship);
  };

  useEffect(() => {
    if (!queryParams.code && !reference?.id) onChangePage(Pages.CheckCode);
  }, [queryParams.code]);

  useEffect(() => {
    if (isPreview) onChangePage(Pages.Questions);
    if (isManualInput) changeManualInputPageToDefault();
    else if (reference?.id) changePageToDefault();
  }, [reference?.id]);

  useEffect(() => {
    if (questions?.length) {
      setQuestionPages(fromQuestionsToPages(questions, isPreview));
      if (!questionPages.length) {
        setCurrentQuestionPageIndex(defaultCurrentPageIndex(fromQuestionsToPages(questions, isPreview)));
      }
    }
  }, [questions]);

  const prevConfirming = usePrevious(isConfirming);
  useEffect(() => {
    if (prevConfirming) {
      onChangePage(Pages.Questions);
    }
  }, [isConfirming]);

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = 0;
    }
  }, [currentProspectPage, currentQuestionPageIndex, currentPage, currentIntroPage]);

  const onStartSurvey = () => {
    if (!isPreview) startSurvey({ isManualInput, id: reference.id });
    onChangePage(Pages.ConfirmContactInfo);
  };

  const onFinishSurvey = () => onChangePage(Pages.Prospect);

  const onPrevQuestionPage = () => (currentQuestionPageIndex > 0
    ? onChangeQuestionPageIndex(currentQuestionPageIndex - 1, true)
    : onChangePage(Pages.ConfirmRelationship, true));

  const onNextQuestionPage = () => (currentQuestionPageIndex < questionPages.length - 1
    ? onChangeQuestionPageIndex(currentQuestionPageIndex + 1)
    : onFinishSurvey());

  const goToPrevPage = () => {
    switch (currentPage) {
      case Pages.Intro:
        if (currentIntroPage === IntroPages.FirstDecline || currentIntroPage === IntroPages.ContentWaiver) {
          onChangeIntroPage(IntroPages.WelcomeScreen, true);
        } else if (currentIntroPage === IntroPages.SecondDecline) {
          onChangeIntroPage(IntroPages.FirstDecline);
        }
        break;
      case Pages.ConfirmContactInfo:
        onChangePage(Pages.Intro, true);
        onChangeIntroPage(IntroPages.ContentWaiver, true);
        break;
      case Pages.ConfirmRelationship:
        onChangePage(Pages.ConfirmContactInfo, true);
        break;
      case Pages.Questions:
        onPrevQuestionPage();
        break;
      default:
        break;
    }
  };

  const goBackHandler = useCallback((event: PopStateEvent) => {
    const isBack = !event.state;
    if (isBack) {
      history.goForward();
      goToPrevPage();
    }
  }, [currentPage, currentIntroPage, currentQuestionPageIndex]);

  useEffect(() => {
    window.addEventListener('popstate', goBackHandler);
    return () => {
      window.removeEventListener('popstate', goBackHandler);
    };
  }, [goBackHandler]);

  const getCurrentPage = () => {
    switch (currentPage) {
      case Pages.CheckCode:
        return (
          <CheckCode
            onStart={checkCode}
            errorCode={errorCode as string}
            isChecking={isChecking}
          />
        );
      case Pages.Intro:
        return (
          <Intro
            onContinue={onStartSurvey}
            onRejectSurvey={onFinishSurvey}
            currentPage={currentIntroPage}
            setCurrentPage={onChangeIntroPage}
          />
        );
      case Pages.ConfirmContactInfo:
        return (
          <ConfirmContactInfoForm
            candidateFirstName={candidateFirstName}
            onSubmit={updatedRefInfo => {
              setContactInfo(updatedRefInfo);
              onChangePage(Pages.ConfirmRelationship);
            }}
            reference={reference}
            goBack={() => { onChangePage(Pages.Intro, true); onChangeIntroPage(IntroPages.ContentWaiver, true); }}
          />
        );
      case Pages.ConfirmRelationship:
        return (
          <ConfirmRelationshipForm
            goBack={() => onChangePage(Pages.ConfirmContactInfo, true)}
            onSubmit={data => confirmReference({ data, isManualInput })}
          />
        );
      case Pages.Questions:
        return (
          <QuestionsContainer
            isPreview={isPreview}
            currentPageIndex={currentQuestionPageIndex}
            pages={questionPages}
            onNextPage={onNextQuestionPage}
            goBack={onPrevQuestionPage}
          />
        );
      case Pages.Prospect: return (
        <Prospect
          isPreview={isPreview}
          onThank={() => onChangePage(Pages.ThankPage)}
          currentPage={currentProspectPage}
          setCurrentPage={setCurrentProspectPage}
        />
      );
      case Pages.ThankPage: return <ThankPage isPreview={isPreview} />;
      default: return <></>;
    }
  };

  const questionPageContent = questionPages[currentQuestionPageIndex]?.content as IQuestionForReference;
  const circleColor = (currentPage !== Pages.ThankPage && settings) ? settings['--circle-color'] : undefined;

  return (
    <LoaderWrapper loading={Boolean(!isPreview && !reference?.id && queryParams.code)}>
      <div ref={ref} className={styles.bd} style={{ backgroundImage: getBackgroundURL(circleColor) }}>
        <div className={styles.container}>
          <TransitionGroup component={null}>
            <CSSTransition
              key={`${currentPage}.${currentIntroPage}.${currentQuestionPageIndex}.${currentProspectPage}`}
              timeout={600}
              classNames={`slide-${animationClass}`}
            >
              <div className={styles.pageContainer}>
                {getCurrentPage()}
              </div>
            </CSSTransition>
          </TransitionGroup>
          {currentPage === Pages.Questions && (
            <>
              <div className={styles.header}>
                {logo && (
                  <img className={styles.logo} src={logo} alt="" />
                )}
                <div className={`${styles.progressWrp} d-flex align-items-center justify-content-around`}>
                  <span className={`avenir-font t-16 ${styles.progressLabel}`}>
                    <FormattedMessage id="positionTable.progress" defaultMessage="Progress" />
                  </span>
                  <ProgressBar
                    value={currentQuestionPageIndex}
                    max={questionPages.length || 1}
                    className={styles.progressBar}
                  />
                </div>
              </div>
              <div className="survey-back-bttn">
                <IconButton
                  onClick={onPrevQuestionPage}
                  icon={<Icon icon="arrowLeft" className="backButtonIcon" />}
                  variant="bordered"
                  disabled={isPreview && !currentQuestionPageIndex}
                />
              </div>
              {(isPreview || Boolean(questionPageContent?.answer)) && (
                <div className="survey-next-bttn">
                  <IconButton
                    onClick={onNextQuestionPage}
                    icon={<Icon icon="arrowRight" className="backButtonIcon" />}
                    variant="bordered"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </LoaderWrapper>
  );
};

const mapStateToProps = ({
  refSurvey: {
    surveyStartDate,
    surveyCompleteDate,
    reference,
    error,
    isConfirming,
    questions,
    candidate,
    isChecking,
    errorCode,
    isManualInput
  },
  workspace: { settings, logo }
}: IAppState) => ({
  surveyStartDate,
  surveyCompleteDate,
  reference,
  error,
  isConfirming,
  questions,
  candidate,
  candidateFirstName: candidate.firstName,
  settings,
  logo,
  isChecking,
  errorCode,
  isManualInput
});

const mapDispachToProps = {
  fetchReferenceSurvey: fetchReferenceSurveyRoutine,
  startSurvey: startSurveyRoutine,
  confirmReference: confirmReferenceRoutine,
  checkKey: checkSurveyKeyRoutine,
  checkCode: checkSurveyCodeRoutine,
  setContactInfo: setRefContactInfoRoutine,
  setWorkspaceColors: setWorkspaceColorsRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(withRouter(Survey));
